import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { updateOrganisation } from '../api/organisationAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { getById } from '../api';
import { fetchData } from '../utils/apiHelpers';
import getLogoSrc from '../utils/logoUtils';

const OrganisationLayer = () => {
  const [orgData, setOrgData] = useState(null);
  const [originalOrgData, setOriginalOrgData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const userProfile = useUserProfile();
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    fetchData(getById, { apiName: 'organisations', id: 1 }, (data) => {
      setOrgData(data);
      setOriginalOrgData(data);
    });
  }, []);

  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > maxFileSize) {
        alert('File is too large. Maximum allowed size is 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        setOrgData((prev) => ({ ...prev, orgLogo: base64 }));
      };
      reader.onerror = () => {
        console.error('File reading failed');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setOrgData({ ...orgData, [id]: value });
  };

  const toggleEdit = () => {
    setEditMode(true);
  };

  const handleReset = () => {
    setOrgData(originalOrgData);
    setEditMode(false);
  };

  const handleSave = (e) => {
    e.preventDefault();
    updateOrganisation(1, orgData)
      .then((updated) => {
        setOrgData(updated);
        setOriginalOrgData(updated);
        setEditMode(false);
      })
      .catch((err) => console.error('Error updating organisation:', err));
  };

  if (!orgData) {
    return <div>Loading...</div>;
  }

  const logoSrc = getLogoSrc(orgData.orgLogo) || 'assets/images/user.png';

  return (
    <div className="card h-100 p-24 radius-12">
      <form onSubmit={handleSave}>
        <div className="row mt-0">
          <div className="col-md-12">
            <div className="text-center mb-24 position-relative d-inline-block">
              {editMode && (
                <>
                  <input
                    type="file"
                    id="logoUpload"
                    accept=".png, .jpg, .jpeg"
                    hidden
                    onChange={handleImageChange}
                  />
                  <label
                    htmlFor="logoUpload"
                    className="position-absolute end-0 bottom-0 w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 rounded-circle cursor-pointer"
                  >
                    <Icon icon="solar:camera-outline" />
                  </label>
                </>
              )}
              <img
                src={logoSrc}
                alt="Organisation Logo"
                style={{ height: '100px', objectFit: 'cover' }}
              />
            </div>
          </div>
          <div className="col-md-10">
            <div className="mb-3">
              <label htmlFor="orgName" className="form-label">
                Organisation Name
              </label>
              <input
                type="text"
                className="form-control"
                id="orgName"
                value={orgData.orgName || ''}
                onChange={handleInputChange}
                disabled={!editMode}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="orgWebsite" className="form-label">
                Website
              </label>
              <input
                type="text"
                className="form-control"
                id="orgWebsite"
                value={orgData.orgWebsite || ''}
                onChange={handleInputChange}
                disabled={!editMode}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="orgDescription" className="form-label">
                Description
              </label>
              <textarea
                className="form-control"
                id="orgDescription"
                rows="3"
                value={orgData.orgDescription || ''}
                onChange={handleInputChange}
                disabled={!editMode}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="orgAddress" className="form-label">
                Address
              </label>
              <input
                type="text"
                className="form-control"
                id="orgAddress"
                value={orgData.orgAddress || ''}
                onChange={handleInputChange}
                disabled={!editMode}
              />
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="orgCity" className="form-label">
                    City
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="orgCity"
                    value={orgData.orgCity || ''}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="orgState" className="form-label">
                    State
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="orgState"
                    value={orgData.orgState || ''}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="orgCountry" className="form-label">
                    Country
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="orgCountry"
                    value={orgData.orgCountry || ''}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="orgZip" className="form-label">
                    Zip
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="orgZip"
                    value={orgData.orgZip || ''}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="orgPhone" className="form-label">
                Phone
              </label>
              <input
                type="text"
                className="form-control"
                id="orgPhone"
                value={orgData.orgPhone || ''}
                onChange={handleInputChange}
                disabled={!editMode}
              />
            </div>
          </div>
        </div>
        {userProfile?.privileges?.includes('organisations:update') && (
          editMode ? (
            <div className="d-flex justify-content-end gap-2 mt-24">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Save
              </button>
            </div>
          ) : (
            <div className="d-flex justify-content-end gap-2 mt-24">
              <button
                type="button"
                className="btn btn-primary"
                onClick={toggleEdit}
              >
                Edit Details
              </button>
            </div>
          )
        )}

      </form>
    </div>
  );
};

export default OrganisationLayer;
